import { Injectable, OnDestroy } from '@angular/core';

import { BehaviorSubject, filter, Observable, of, Subscription } from 'rxjs';
import moment, { DurationInputArg2 } from 'moment-timezone';

import { BlockModel } from '../../../Common/PageBuilder/models/block.model';
import { DataPointModel } from '../../../Common/Charts/models/data-point.model';
import { ReportBlockTypeEnum } from '../enums/report-block-type.enum';
import { ReportSeriesModel } from '../models/report-series.model';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { PreferencesModel } from '../../../Core/Users/models/preferences.model';
import { ReportDateRangeModel } from '../models/report-date-range.model';
import { DateQueryTypeEnum } from '../../../api/mentions/enum/date-query-type.enum';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { RelativeTimezoneDateTypeEnum } from '../../../api/mentions/enum/relative-timezone-date-type.enum';
import { ChartTimeIntervalsEnum } from '../enums/chart-time-intervals.enum';
import { ChartDisplayElementModel } from '../models/chart-display-element.model';
import { reportsMockDataLabelMap, ReportsMockDataLabelTypesEnum } from './report-series-preview.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { ReportsDataMappingKeysEnum } from '../models/reports-block-mapping-configuration.model';
import { MonitorConfigService } from '../../../Common/MonitorShared/services/monitor-config.service';
import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';
import { mediaTypeToPieMap } from './reports-series-mapper.service';
import { safeDivide } from '../../../Core/utils';
import { ExternalChartMetricsEnum } from '../enums/external-chart-metrics.enum';
import { GoogleAnalyticsReportTypesEnum } from '@api/externalData/enums/google-analytics-report-types.enum';
import { ChartMetricsEnum } from '../enums/chart-metrics.enum';
import { GeographyTypesEnum } from '../../../api/journodex/enum/geography-types.enum';
import { MapChartColorSchemeEnum } from '../../../Common/Charts/models/map-chart.model';
import { chartColors } from '../../../Common/Charts/enums/chart-colors.enum';
import { JournodexGeographicEntityModel } from '../../../Common/Journodex/models/journodex-geographic-entity.model';
import { JournodexGeographyModel } from '../../../Common/Journodex/models/journodex-geography.model';
import { JournodexRegionsService } from '../../../Common/Journodex/services/journodex-regions.service';
import { CoverageMetricChartDisplayElement } from '../models/coverage-metric-chart-display-element.model';
import { SearchCriteriaQuerySentimentsEnum } from '@api/mentions/enum/search-criteria-query-sentiments.enum';
import { chartMetricsToSentimentsMap, sentimentPercentMetrics } from '../maps/chart-metrics-to-sentiments.map';


@Injectable()
export class ReportsSeriesPreviewNewService implements OnDestroy {

    preferences: PreferencesModel;
    hasTiktok: boolean = false;
    currentTimestamp: number;

    subscriptions: Subscription[] = [];

    readonly blockTypesToMockDataLabelTypesMap = new Map<ReportBlockTypeEnum, ReportsMockDataLabelTypesEnum>([
        [ ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME, ReportsMockDataLabelTypesEnum.authorNames ],
        [ ReportBlockTypeEnum.TOP_10_OUTLETS, ReportsMockDataLabelTypesEnum.outletNames ],
        [ ReportBlockTypeEnum.TOP_HASHTAGS, ReportsMockDataLabelTypesEnum.hashtags ],
        [ ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS, ReportsMockDataLabelTypesEnum.socialMedia ]
    ]);

    constructor(
        public currentUserStateService: CurrentUserStateService,
        public localizationService: LocalizationService,
        public monitorConfigService: MonitorConfigService,
        public journodexSearchService: JournodexRegionsService
    ) {
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => {
                this.preferences = prefs;
            }),
            this.monitorConfigService.getAvailableMediaTypes().subscribe(mediaTypes => {
                this.hasTiktok = mediaTypes.includes(MediaTypeEnum.TIKTOK);
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(x => x.unsubscribe());
    }

    getPreviewSeries(series: ReportSeriesModel[], block: BlockModel): ReportSeriesModel[] {
        const newSeries: ReportSeriesModel[] = [];
        this.currentTimestamp = Date.now();
        series.forEach(s => {
            const reportSeries = s.clone();
            const data = this.getPreviewData(s, block);
            if (s.hasPreviousPeriod) {
                const previousData = this.getPreviewData(s, block);
                reportSeries.previousPeriodDataProvider = new BehaviorSubject(previousData as DataPointModel[]);
                data.forEach(point => {
                    point.mappingKeys['date'] = 1;
                    point.mappingKeys['period'] = 1;
                });
            }
            reportSeries.dataProvider = (data as any[]).length ?
                new BehaviorSubject(data as DataPointModel[]) : (data as BehaviorSubject<DataPointModel[]>);
            newSeries.push(reportSeries);
        });

        return newSeries;
    }

    private getPreviewData(series: ReportSeriesModel, block: BlockModel): DataPointModel[] | BehaviorSubject<DataPointModel[]> {
        switch (block.type.key as ReportBlockTypeEnum) {
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
                return this.mockOverTimeData(series, 1, null, (block.displayElement as ChartDisplayElementModel)?.timeInterval);
            case ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME:
            case ReportBlockTypeEnum.TOP_10_OUTLETS:
            case ReportBlockTypeEnum.TOP_HASHTAGS:
            case ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS:
                return this.mockTop10Data(block.type.key as ReportBlockTypeEnum);
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN_COMPARISON:
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN_COMPARISON:
                return this.mockStackedColumnData(series);
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN:
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN:
                return this.mockStackedColumnData(series);
            case ReportBlockTypeEnum.SHARE_OF_VOICE:
            case ReportBlockTypeEnum.COVERAGE_COMPARISON:
                return this.mockShareOfVoiceData(series);
            case ReportBlockTypeEnum.SOCIAL_COVERAGE_BY_NETWORK:
                return this.mockSocialCoverageByNetworkData(series);
            case ReportBlockTypeEnum.SENTIMENT_TREND:
                return this.mockSentimentOverTimeData(series);
            case ReportBlockTypeEnum.TOP_REFERRAL_SOURCES:
                return this.mockTopReferralSourcesData(series);
            case ReportBlockTypeEnum.REFERRAL_TREND:
                return this.mockReferralTrendData(series, (block.displayElement as ChartDisplayElementModel)?.timeInterval);
            case ReportBlockTypeEnum.COVERAGE_IMPACT_TREND:
                return this.mockCoverageImpactTrendData(series, (block.displayElement as ChartDisplayElementModel)?.timeInterval);
            case ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME:
                return this.mockLocationData(series, block);
            case ReportBlockTypeEnum.PROMINENCE_OVER_TIME:
                return this.mockOverTimeData(series, 0.04, 5);
            case ReportBlockTypeEnum.COVERAGE_METRIC:
                const metric = (block.displayElement as CoverageMetricChartDisplayElement).metric;
                if (sentimentPercentMetrics.includes(metric)) {
                    series.sentiments = Object.values(SearchCriteriaQuerySentimentsEnum);
                    return this.mockSentimentOverTimeData(series, 1, null, null, metric);
                } else {
                    return this.mockOverTimeData(series, 1, null, (block.displayElement as ChartDisplayElementModel)?.timeInterval);
                }
        }

        return [];
    }

    mockSentimentOverTimeData(
        series: ReportSeriesModel,
        factor: number = 1,
        maxValue?: number,
        timeInterval?: ChartTimeIntervalsEnum,
        selectedMetric?: ChartMetricsEnum
    ): DataPointModel[] {
        let dateRange = series.dateRange;

        if (!dateRange) {
            dateRange = new ReportDateRangeModel();
            dateRange.timezone = this.preferences.timeZone;
        }

        let stubData = [];
        const sliceUnit = this.determineSliceUnit(dateRange, timeInterval);
        let startTime;
        let endTime;
        if (dateRange.type !== DateQueryTypeEnum.ALL) {
            startTime = this.getStartTime(dateRange);
            endTime = this.getEndTime(dateRange);
        } else {
            const durationBase = this.getAllRangeDurationBase(timeInterval);
            // The values here allow for a maximum start time of 60 days ago, lasting at least 15 days and ending at most with the current date
            startTime = moment(this.currentTimestamp).subtract((Math.random() * 45) + durationBase, 'days').toDate().getTime();
            endTime = moment(startTime).add((Math.random() * 44) + durationBase, 'days').toDate().getTime();
        }

        // Relative ranges should end before current time, or they'll show the current date as a bullet, trim 10ms off to account for it
        if (dateRange.type !== DateQueryTypeEnum.ABSOLUTE) {
            endTime -= 10;
        }

        let lastValue: number = null;
        const timeSlicesMap = new Map<number, any[]>();

        series.sentiments.forEach(sentiment => {
            let currentTime = startTime;
            for (let index = 0; currentTime <= endTime; currentTime = moment(currentTime).add(1, sliceUnit as DurationInputArg2).toDate().getTime()) {
                let newValue: number;
                if (lastValue !== null) {
                    // Base the new value off the old one, new value = oldValue +- (15 * factor)
                    // To minimize large deltas and create a smoother line
                    newValue = lastValue + ((Math.round(Math.random() * 30) - 15) * factor);
                    newValue = Math.max(0, newValue);
                } else {
                    newValue = Math.round(Math.random() * 100) * factor;
                }

                if (maxValue) {
                    newValue = Math.min(newValue, maxValue);
                }
                const adjustedCurrentTime = moment(moment(currentTime).tz(this.preferences.timeZone));
                const valueLabel = series.chartType === ReportBlockTypeEnum.SENTIMENT_TREND
                    ? this.localizationService.transform(`Sentiment.${sentiment}`)
                    : sentiment;
                const data = {
                    label: sliceUnit === 'hours' ? adjustedCurrentTime.format('HH:mm') : adjustedCurrentTime.toDate().toDateString(),
                    mappingKeys: {
                        [ReportsDataMappingKeysEnum.DATE]: adjustedCurrentTime.valueOf(),
                        [ReportsDataMappingKeysEnum.SENTIMENT]: sentiment,
                        [ReportsDataMappingKeysEnum.DATE_INDEX]: index,
                        [ReportsDataMappingKeysEnum.PERIOD]: 0
                    },
                    value: newValue,
                    percent: 0, // Temporary, will be normalized later
                    valueLabel: valueLabel,
                    // this mentionCount is being used to store the total number of sentiment mentions, instead of individual
                    mentionCount: Math.round(Math.random() * 100),
                    rawStartDate: currentTime,
                    rawEndDate: moment(currentTime).add(1, sliceUnit as DurationInputArg2).toDate().getTime()
                };

                if (!timeSlicesMap.has(currentTime)) {
                    timeSlicesMap.set(currentTime, []);
                }

                timeSlicesMap.get(currentTime)?.push(data);
                lastValue = newValue;
                index++;
            }
        });

        // Normalize percentages
        timeSlicesMap.forEach(dataArray => {
            const total = dataArray.reduce((sum, d) => sum + d.value, 0);
            dataArray.forEach(d => {
                d.percent = total === 0 ? 0 : (d.value / total) * 100;
                d.mentionCount = total;
                stubData.push(d);
            });
        });

        if (series.chartType === ReportBlockTypeEnum.COVERAGE_METRIC && selectedMetric) {
            if (sentimentPercentMetrics.includes(selectedMetric)) {
                stubData.forEach((x, i) => {
                    const mentionCount = x.value;
                    x.value = x.percent;
                    x['secondaryValue'] = mentionCount;
                });
            }
            stubData = stubData.filter(x => x.mappingKeys[ReportsDataMappingKeysEnum.SENTIMENT] === chartMetricsToSentimentsMap.get(selectedMetric));
            stubData.forEach((x, i) => x['bulletDisabled'] = i < stubData.length - 1);
        }

        return stubData;
    }

    mockOverTimeData(series: ReportSeriesModel, factor: number = 1, maxValue?: number, timeInterval?: ChartTimeIntervalsEnum): DataPointModel[] {
        let dateRange = series.dateRange;

        if (!dateRange) {
            dateRange = new ReportDateRangeModel();
            dateRange.timezone = this.preferences.timeZone;
        }

        const stubData = [];
        const sliceUnit = this.determineSliceUnit(dateRange, timeInterval);
        let startTime;
        let endTime;
        if (dateRange.type !== DateQueryTypeEnum.ALL) {
            startTime = this.getStartTime(dateRange);
            endTime = this.getEndTime(dateRange);
        } else {
            const durationBase = this.getAllRangeDurationBase(timeInterval);
            // Use a fixed window so all comparison series share the same dates; random windows produce no overlapping dates and no visible lines
            endTime = moment(this.currentTimestamp).toDate().getTime();
            startTime = moment(endTime).subtract(durationBase, 'days').toDate().getTime();
        }

        // Relative ranges should end before current time, or they'll show the current date as a bullet, trim 10ms off to account for it
        if (dateRange.type !== DateQueryTypeEnum.ABSOLUTE) {
            endTime -= 10;
        }

        // const sliceUnit: string = sliceSize === 3600000 ? 'hours' : 'days';
        let lastValue: number = null;
        const metrics = series.metrics?.length > 0 ? series.metrics : [series.metric];
        metrics.forEach(metric => {
            let currentTime = startTime;
            for (let index = 0; currentTime <= endTime; currentTime = moment(currentTime).add(1, sliceUnit as DurationInputArg2).toDate().getTime()) {
                let newValue: number;
                if (lastValue !== null) {
                    // Base the new value off the old one, new value = oldValue +- (15 * factor)
                    // To minimize large deltas and create a smoother line
                    newValue = lastValue + ((Math.round(Math.random() * 30) - 15) * factor);
                    if (newValue < 0) {
                        newValue = 0;
                    }
                } else {
                    newValue = Math.round(Math.random() * 100) * factor;
                }
                if (maxValue) {
                    newValue = Math.min(newValue, maxValue);
                }
                const adjustedCurrentTime = moment(moment(currentTime).tz(this.preferences.timeZone));
                const valueLabel = series.chartType === ReportBlockTypeEnum.COVERAGE_OVER_TIME
                    ? this.localizationService.transform('Reports.formField.analysis.' + metric) : series.label;
                const data = {
                    label: sliceUnit === 'hours' ? adjustedCurrentTime.format('HH:mm') : adjustedCurrentTime.toDate().toDateString(),
                    mappingKeys: {
                        date: adjustedCurrentTime.valueOf(),
                        source: series.label,
                        metric: metric,
                        [ReportsDataMappingKeysEnum.DATE_INDEX]: index,
                        [ReportsDataMappingKeysEnum.PERIOD]: 0
                    },
                    value: newValue,
                    valueLabel: valueLabel,
                    mentionCount: Math.round(Math.random() * 100),
                    rawStartDate: currentTime,
                    rawEndDate: moment(currentTime).add(1, sliceUnit as DurationInputArg2).toDate().getTime()
                };
                if (series.chartType === ReportBlockTypeEnum.COVERAGE_METRIC) {
                    data['bulletDisabled'] = true;
                    data['secondaryValue']= Math.round(Math.random() * 100);
                }
                stubData.push(data);
                lastValue = newValue;
                index++;
            }
            if (series.chartType === ReportBlockTypeEnum.COVERAGE_METRIC && stubData.length > 0) {
                stubData[stubData.length - 1]['bulletDisabled'] = false;
            }
        });

        return stubData;
    }

    mockTop10Data(blockType: ReportBlockTypeEnum): DataPointModel[] {
        const blockTypesToMappingKeysMap = new Map<ReportBlockTypeEnum, ReportsDataMappingKeysEnum>([
            [ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME, ReportsDataMappingKeysEnum.AUTHOR],
            [ReportBlockTypeEnum.TOP_10_OUTLETS, ReportsDataMappingKeysEnum.OUTLET],
            [ReportBlockTypeEnum.TOP_HASHTAGS, ReportsDataMappingKeysEnum.HASHTAG],
            [ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS, ReportsDataMappingKeysEnum.REFERRER]
        ]);
        const labels = reportsMockDataLabelMap.get(this.blockTypesToMockDataLabelTypesMap.get(blockType));
        const totalResults = 1000 + Math.round(Math.random() * 1000); // Maximum of 2000
        const stubData: DataPointModel[] = labels.map((name, index) => {
            const count = Math.round(Math.random() * 100);
            const entity = {
                label: name,
                mappingKeys: {
                    [blockTypesToMappingKeysMap.get(blockType)]: name
                },
                value: count
            };
            if (index === 0) {
                entity['uuid'] = 1;
            }

            return entity;
        });

        stubData.forEach((data) => {
            data.percent = parseFloat((safeDivide(data.value, totalResults) * 100).toFixed(1));
        });

        stubData.sort((a, b) => b.value - a.value);

        return stubData;
    }

    mockStackedColumnData(series: ReportSeriesModel): DataPointModel[] {
        let categories = [];
        const newDataPoints = [];
        if (series.chartType === ReportBlockTypeEnum.SENTIMENT_BREAKDOWN
            || series.chartType === ReportBlockTypeEnum.SENTIMENT_BREAKDOWN_COMPARISON
        ) {
            categories = series.sentiments;
        } else {
            categories = Array.from(mediaTypeToPieMap);
            if (!this.hasTiktok) {
                categories = categories.filter(([key]) => key !== MediaTypeEnum.TIKTOK);
            }

            if (!!series.styles.networkSelection) {
                categories = categories.filter(([key]) => series.styles.networkSelection.includes(key));
            }
        }

        categories.forEach(category => {
            const count = Math.round(Math.random() * 100);
            const newDataPoint: DataPointModel = {
                label: series.label,
                mappingKeys: {
                    source: series.label
                },
                value: count,
                mentionCount: count
            };
            if (series.chartType === ReportBlockTypeEnum.SENTIMENT_BREAKDOWN
                || series.chartType === ReportBlockTypeEnum.SENTIMENT_BREAKDOWN_COMPARISON
            ) {
                newDataPoint.mappingKeys['sentiment'] = category;
                newDataPoint.valueLabel = this.localizationService.transform('Sentiment.' + category);
            } else {
                newDataPoint.mappingKeys['mediaType'] = category[0];
                newDataPoint.valueLabel = this.localizationService.transform(category[1].label);
            }

            newDataPoints.push(newDataPoint);
        });
        const totalResults = newDataPoints.reduce((x, y) => x + y.mentionCount, 0);
        newDataPoints.forEach(dataPoint => {
            dataPoint.percent = parseFloat((safeDivide(dataPoint.value, totalResults) * 100).toFixed(1));
        });

        return newDataPoints;
    }

    mockShareOfVoiceData(seriesItem: ReportSeriesModel): DataPointModel[] {
        const stubSeries = new ReportSeriesModel(seriesItem.toApiModel(), seriesItem.chartType);
        const newDataPoints: DataPointModel[] = [];

        newDataPoints.push({
            mappingKeys: {
                source: stubSeries.label,
                date: 0
            },
            value: Math.round(Math.random() * 100)
        });

        return newDataPoints;
    }

    mockSocialCoverageByNetworkData(series: ReportSeriesModel): DataPointModel[] {
        if (series.styles.networkSelection) {
            const totalResults = 1000 + Math.round(Math.random() * 1000); // Maximum of 2000
            const stubData: DataPointModel[] = series.styles.networkSelection.map(
                mediaType => {
                    let labelMapKey = mediaType;
                    if (mediaType === MediaTypeEnum.TWITTER) {
                        labelMapKey = 'TWEET';
                    }
                    return {
                        mappingKeys: {
                            mediaType: mediaType,
                            date: 0
                        },
                        value: Math.round(Math.random() * 100),
                        valueLabel: this.localizationService.transform(`MediaType.${labelMapKey}`),
                    };
                }
            );

            stubData.forEach(
                (data) => {
                    data.percent = ((data.value / totalResults) * 100).toFixed(2);
                }
            );

            return stubData;
        }

        return [];
    }

    mockTopReferralSourcesData(series: ReportSeriesModel): DataPointModel[] {
        const dataPoints: DataPointModel[] = [];
        const metrics = [];
        switch (series.metric) {
            case ExternalChartMetricsEnum.sessionsNewReturning:
                metrics.push(ExternalChartMetricsEnum.sessionsNew, ExternalChartMetricsEnum.sessionsReturning);
                break;
            case ExternalChartMetricsEnum.usersNewReturning:
                metrics.push(ExternalChartMetricsEnum.usersNew, ExternalChartMetricsEnum.usersReturning);
                break;
            case ExternalChartMetricsEnum.sessions:
            case ExternalChartMetricsEnum.users:
                metrics.push(series.metric);
                break;
        }
        reportsMockDataLabelMap.get(ReportsMockDataLabelTypesEnum.referralSources).forEach(source => {
            let totalResults = 0;
            metrics.forEach(metric => {
                const value = Math.round(Math.random() * 100);
                dataPoints.push({
                    label: source,
                    mappingKeys: {
                        [ReportsDataMappingKeysEnum.SOURCE]: source,
                        [ReportsDataMappingKeysEnum.GA_METRIC]: metric,
                    },
                    value: value
                });
                totalResults += value;
            });
        });

        return dataPoints;
    }

    mockReferralTrendData(series: ReportSeriesModel, timeInterval: ChartTimeIntervalsEnum): DataPointModel[] {
        let dateRange = series.dateRange;
        if (!dateRange) {
            dateRange = new ReportDateRangeModel();
            dateRange.timezone = this.preferences.timeZone;
        }
        const sliceUnit = this.determineSliceUnit(dateRange, timeInterval);
        let currentTime = this.getStartTime(dateRange);
        let endTime = this.getEndTime(dateRange);
        if (dateRange.type !== DateQueryTypeEnum.ABSOLUTE) {
            endTime -= 10;
        }
        const dataPoints = [];
        for (; currentTime <= endTime; currentTime = moment(currentTime).add(1, sliceUnit as DurationInputArg2).toDate().getTime()) {
            series.referralTypes.filter(x => x !== GoogleAnalyticsReportTypesEnum.TOTAL_REFERRALS).forEach(referralType => {
                const value =  Math.round(Math.random() * 100 + 50);
                dataPoints.push({
                    mappingKeys: {
                        [ReportsDataMappingKeysEnum.DATE]: currentTime,
                        [ReportsDataMappingKeysEnum.REFERRER_TYPE]: referralType
                    },
                    value: value,
                    valueLabel: this.localizationService.transform(`Reports.googleAnalyticsCharts.${referralType}`),
                    rawStartDate: currentTime,
                    timeScale: sliceUnit
                });
            });
            let total = 0;
            for (let i = 0; i < series.referralTypes.length - 1; i++) {
                total += dataPoints[dataPoints.length - 1 - i].value;
            }
            dataPoints.push({
                mappingKeys: {
                    [ReportsDataMappingKeysEnum.DATE]: currentTime,
                    [ReportsDataMappingKeysEnum.REFERRER_TYPE]: GoogleAnalyticsReportTypesEnum.TOTAL_REFERRALS
                },
                value: total,
                valueLabel: this.localizationService.transform(`Reports.googleAnalyticsCharts.${GoogleAnalyticsReportTypesEnum.TOTAL_REFERRALS}`),
                rawStartDate: currentTime,
                timeScale: sliceUnit
            });
        }

        return dataPoints;
    }

    mockCoverageImpactTrendData(series: ReportSeriesModel, timeInterval: ChartTimeIntervalsEnum): DataPointModel[] {
        const dataPoints = [];
        let dateRange = series.dateRange;
        if (!dateRange) {
            dateRange = new ReportDateRangeModel();
            dateRange.timezone = this.preferences.timeZone;
        }
        const sliceUnit = this.determineSliceUnit(dateRange, timeInterval);
        let currentTime = this.getStartTime(dateRange);
        let endTime = this.getEndTime(dateRange);
        if (dateRange.type !== DateQueryTypeEnum.ABSOLUTE) {
            endTime -= 10;
        }
        for (; currentTime <= endTime; currentTime = moment(currentTime).add(1, sliceUnit as DurationInputArg2).toDate().getTime()) {
            const metrics = series.referralTypes?.length > 0 ? series.referralTypes : [ChartMetricsEnum.volumeOfMentions];
            metrics.forEach(key => {
                const value =  Math.round(Math.random() * 100 + 50);
                dataPoints.push({
                    mappingKeys: {
                        [ReportsDataMappingKeysEnum.DATE]: currentTime,
                        [ReportsDataMappingKeysEnum.COVERAGE_IMPACT_TREND_METRIC]: key,
                    },
                    value: value,
                    valueLabel: series.referralTypes?.length > 0 ?
                        this.localizationService.transform(`Reports.googleAnalyticsCharts.${key}`) : series.label,
                    rawStartDate: currentTime,
                    timeScale: sliceUnit
                });
            });
        }

        return dataPoints;
    }

    private mockLocationData(series: ReportSeriesModel, block: BlockModel): DataPointModel[] | BehaviorSubject<DataPointModel[]> {
        const stubSeries = new ReportSeriesModel(series.toApiModel(), series.chartType);
        let data: DataPointModel[] | BehaviorSubject<DataPointModel[]>;

        if (!stubSeries || !stubSeries.styles) {
            console.log('returns prematurally');
            return [];
        }
        stubSeries.dataProvider = new BehaviorSubject(null);
        switch (stubSeries.styles.geographyScale) {
            case GeographyTypesEnum.country:
                data = this.setStubDataForCountry(stubSeries, (block.displayElement as ChartDisplayElementModel).mapChartColorScheme);
                break;
            default:
                data = this.setStubDataForWorld(stubSeries, (block.displayElement as ChartDisplayElementModel).mapChartColorScheme);
        }

        return data;
    }

    /**
     * Sets mock data for a world chart
     */
    setStubDataForWorld(series: ReportSeriesModel, colorScheme: MapChartColorSchemeEnum): DataPointModel[] {
        const stubData: DataPointModel[] = [
            {
                label: 'Canada',
                id: 'CA',
                value: Math.round(Math.random() * 100)
            },
            {
                label: 'United States',
                id: 'US',
                value: Math.round(Math.random() * 100)
            },
            {
                label: 'United Kingdom',
                id: 'GB',
                value: Math.round(Math.random() * 100)
            },
            {
                label: 'Germany',
                id: 'DE',
                value: Math.round(Math.random() * 100)
            },
            {
                label: 'Switzerland',
                id: 'CH',
                value: Math.round(Math.random() * 100)
            },
            {
                label: 'Norway',
                id: 'NO',
                value: Math.round(Math.random() * 100)
            },
            {
                label: 'Nigeria',
                id: 'NG',
                value: Math.round(Math.random() * 100)
            },
            {
                label: 'Turkey',
                id: 'TR',
                value: Math.round(Math.random() * 100)
            },
            {
                label: 'India',
                id: 'IN',
                value: Math.round(Math.random() * 100)
            },
        ];

        if (colorScheme === MapChartColorSchemeEnum.CHOROPLETH) {
            stubData.push(
                {
                    label: 'Spain',
                    id: 'ES',
                    value: Math.round(Math.random() * 100)
                },
                {
                    label: 'Italy',
                    id: 'IT',
                    value: Math.round(Math.random() * 100)
                },
                {
                    label: 'France',
                    id: 'FR',
                    value: Math.round(Math.random() * 100)
                },
                {
                    label: 'Mexico',
                    id: 'MX',
                    value: Math.round(Math.random() * 100)
                },
                {
                    label: 'France',
                    id: 'FR',
                    value: Math.round(Math.random() * 100)
                },
                {
                    label: 'Russia',
                    id: 'RU',
                    value: Math.round(Math.random() * 100)
                },
                {
                    label: 'China',
                    id: 'CN',
                    value: Math.round(Math.random() * 100)
                },
                {
                    label: 'Australia',
                    id: 'AU',
                    value: Math.round(Math.random() * 100)
                },
                {
                    label: 'South Afria',
                    id: 'ZA',
                    value: Math.round(Math.random() * 100)
                },
                {
                    label: 'Brazil',
                    id: 'BR',
                    value: Math.round(Math.random() * 100)
                },
                {
                    label: 'Argentina',
                    id: 'AR',
                    value: Math.round(Math.random() * 100)
                }
            );
        }

        stubData.forEach(x => {
            x.mappingKeys = {};
            x.mappingKeys[ReportsDataMappingKeysEnum.LOCATION] = x.id;
        });

        return this.filterDataPointModels(stubData, colorScheme);
    }

    /**
     * Sets mock data for a given country
     */
    private setStubDataForCountry(series: ReportSeriesModel, colorScheme: MapChartColorSchemeEnum): BehaviorSubject<DataPointModel[]> {
        if (!series || !series.styles || !series.styles.regionIsoCode) {
            return new BehaviorSubject<DataPointModel[]>([]);
        }
        const dataProvider = new BehaviorSubject<DataPointModel[]>([]);
        const countryJournodexId = +this.journodexSearchService.getCountryByIsoCode(series.styles.regionIsoCode + '').id;

        const geographyModel = new JournodexGeographyModel();
        geographyModel.id = countryJournodexId;
        geographyModel.geographicType = GeographyTypesEnum.country;
        geographyModel.country = <JournodexGeographicEntityModel>{ id: countryJournodexId, name: null, names: null };

        this.journodexSearchService.getChildRegions(geographyModel).subscribe(
            provinces => {
                const stubData: DataPointModel[] = provinces.map(province => {
                    return {
                        label: province.province.name,
                        id: `${series.styles.regionIsoCode}-${province.isoCode}`,
                        mappingKeys: {
                            [ReportsDataMappingKeysEnum.LOCATION]: `${series.styles.regionIsoCode}-${province.isoCode}`
                        },
                        value: Math.round(Math.random() * 100)
                    };
                });
                dataProvider.next(this.filterDataPointModels(stubData, colorScheme));
            }
        );

        return dataProvider;
    }

    //#region private helper methods for mocking data

    /**
     * Sets the dateFacet slice size based on the dateQuery range, if it's greater than 1 day, use days, else, use hours
    */
    private determineSliceUnit(dateQuery: ReportDateRangeModel, timeInterval?: ChartTimeIntervalsEnum): DurationInputArg2 {
        if (timeInterval && timeInterval !== ChartTimeIntervalsEnum.DYNAMIC) {
            // A fixed interval was explicitly selected, so mock data should bucket at that exact granularity
            // instead of estimating one from the date range.
            switch (timeInterval) {
                case ChartTimeIntervalsEnum.WEEK:
                    return 'week';
                case ChartTimeIntervalsEnum.MONTH:
                    return 'month';
                case ChartTimeIntervalsEnum.QUARTER:
                    return 'quarter';
                case ChartTimeIntervalsEnum.YEAR:
                    return 'year';
            }
        }

        if (dateQuery.type === DateQueryTypeEnum.ABSOLUTE) {
            let hoursDifference: number;
            if (dateQuery.absoluteRange) {
                const start = moment(dateQuery.absoluteRange.start);
                const end = moment(dateQuery.absoluteRange.end);

                hoursDifference = moment.duration(end.diff(start)).asHours();
            } else {
                // absoluteDate case
                hoursDifference = 24;
            }

            // use ~100 as the max number of data slices to align with backend
            if (hoursDifference <= 24) {
                return 'hour';
            } else if (hoursDifference <= 24 * 100) {
                return 'day';
            } else if (hoursDifference <= 24 * 7 * 100) {
                return 'week';
            } else if (hoursDifference <= 24 * 365 * 8) {
                return 'month';
            } else {
                return 'quarter';
            }
        } else if (dateQuery.type === DateQueryTypeEnum.RELATIVE) {
            if (Math.abs(dateQuery.relativeEndOffsetH - dateQuery.relativeStartOffsetH) <= 24) {
                // The date range covers 24 hours or less
                return 'hour';
            } else {
                // The date range covers more than 24 hours
                return 'day';
            }
        } else if (dateQuery.type === DateQueryTypeEnum.RELATIVE_TIMEZONE) {
            const hoursDifference = DateQueryModel.getRelativeTimezoneDiffAsHours(dateQuery.relativeTimezoneDateType, dateQuery.timezone);
            if (hoursDifference <= 24 || dateQuery.relativeTimezoneDateType === RelativeTimezoneDateTypeEnum.LAST_24_HOURS) {
                // The date range covers 24 hours or less
                return 'hour';
            } else {
                if (dateQuery.relativeTimezoneDateType === RelativeTimezoneDateTypeEnum.LAST_THREE_MONTHS) {
                    return 'week';
                } else {
                    // The date range covers more than 24 hours
                    return 'day';
                }
            }
        } else {
            // 'ALL' is an unbounded range, so mirror TimeHelper.getDateGranularity's Infinity-hours
            // fallback (3-month/quarter buckets) instead of assuming daily slices
            return 'quarter';
        }
    }

    private getAllRangeDurationBase(timeInterval?: ChartTimeIntervalsEnum): number {
        const targetBucketCount = 8;
        let bucketSizeInDays: number;
        switch (timeInterval) {
            case ChartTimeIntervalsEnum.WEEK:
                bucketSizeInDays = 7;
                break;
            case ChartTimeIntervalsEnum.MONTH:
                bucketSizeInDays = 30;
                break;
            case ChartTimeIntervalsEnum.QUARTER:
                bucketSizeInDays = 90;
                break;
            case ChartTimeIntervalsEnum.YEAR:
                bucketSizeInDays = 365;
                break;
            default:
                bucketSizeInDays = 90;
        }

        return bucketSizeInDays * targetBucketCount;
    }

    private getStartTime(dateQuery: ReportDateRangeModel): number {
        if (dateQuery.type === DateQueryTypeEnum.ABSOLUTE) {
            let start;
            if (dateQuery.absoluteRange) {
                start = moment(dateQuery.absoluteRange.start);
            } else {
                start = moment(dateQuery.absoluteDate);
            }
            return start.toDate().getTime();
        } else if (dateQuery.type === DateQueryTypeEnum.RELATIVE_TIMEZONE) {
            const start = DateQueryModel.getStartDateForRelativeTimezone(dateQuery.relativeTimezoneDateType, dateQuery.timezone);

            return start.toDate().getTime();
        } else {
            const start = moment(moment.now());
            start.add(dateQuery.relativeStartOffsetH, 'hours');
            return start.toDate().getTime();
        }
    }

    private getEndTime(dateQuery: ReportDateRangeModel): number {
        if (dateQuery.type === DateQueryTypeEnum.ABSOLUTE) {
            let end;
            if (dateQuery.absoluteRange) {
                end = moment(dateQuery.absoluteRange.end);
            } else {
                end = moment(dateQuery.absoluteDate);
            }
            return end.toDate().getTime();
        } else if (dateQuery.type === DateQueryTypeEnum.RELATIVE_TIMEZONE) {
            const end = DateQueryModel.getEndDateForRelativeTimezone(dateQuery.relativeTimezoneDateType, dateQuery.timezone);

            return end.toDate().getTime();
        } else {
            const end = moment(moment.now());
            end.add(dateQuery.relativeEndOffsetH, 'hours');
            return end.toDate().getTime();
        }
    }

    /**
     * Filters the data so that only the top 10 items and an aggregate of the others appear in the results
     */
    filterDataPointModels(data: DataPointModel[], colorScheme: MapChartColorSchemeEnum): DataPointModel[] {
        // leave other out, so that it's always added last
        const other: DataPointModel = {
            label: 'Other',
            value: Math.round(Math.random() * 100) + 1,
            color: chartColors.agprOther
        };

        const addOther: boolean = data.length >= 10 && colorScheme !== MapChartColorSchemeEnum.CHOROPLETH;

        let total = 0;
        if (addOther) {
            total = other.value;
        }
        data.forEach((item) => {
            item.value += 1;
            total += item.value;
        });
        data.forEach((item) => {
            item.percent = item.value / total * 100;
        });
        data.sort((a, b) => b.value - a.value);

        other.percent = other.value / total * 100;

        if (addOther) {
            data = data.slice(0, 10);
            data.push(other);
        }

        return data;
    }

    //#endregion

}
